import { forceSimulation, forceManyBody, forceLink, forceCenter, forceCollide } from "d3-force-3d";
import { aggregateAgents, feasibleCandidateTreatments } from "@/lib/decisionGraphLegend";

// The 6 cinematic states. Titles/descriptions are UI copy only — every state is
// entered strictly from REAL canonical event types (no fabricated telemetry).
export const CINEMATIC_STATES = [
  { key: "normal", title: "Normal operations", desc: "Baseline steady-state posture." },
  { key: "event", title: "Event registered", desc: "A capacity / security event enters the record." },
  { key: "mas", title: "MAS mobilized", desc: "The multi-agent system is mobilized to respond." },
  { key: "compare", title: "Compare candidates", desc: "Candidate solutions are generated and compared." },
  { key: "verify", title: "Verify & accept", desc: "The decision is verified and a human accepts it." },
  { key: "outcome", title: "Reveal outcome", desc: "The accepted outcome is committed to the record." },
];

// Canonical event_type → cinematic state index. Additive demo types + existing types.
export const EVENT_STATE = {
  "run.created": 0, "run.authorized": 0, "run.started": 0, "scenario.normal_operations": 0,
  "xius.partner_congestion_detected": 1, "xius.capacity_loss_registered": 1,
  "griffendo.alert_correlation_surge": 1, "griffendo.analyst_capacity_constrained": 1,
  "mas.agents_mobilized": 2, "work.scheduled": 2, "work.started": 2, "dependency.unlocked": 2,
  "solver.started": 3, "candidates.compared": 3, "model.dispatched": 3, "tool.started": 3,
  "tool.completed": 3, "tool.failed": 3,
  "solver.classical_selected": 4, "solver.dwave_submitted": 4, "verification.completed": 4,
  "approval.recorded": 4,
  "work.completed": 5, "run.completed": 5, "report.created": 5, "artifact.created": 5,
};

export function stateForEvent(eventType) {
  return EVENT_STATE[eventType] ?? 0;
}

// The cinematic state at a cursor is the highest state reached by any REAL event
// up to that cursor — monotonic, and derived from nothing but ledger events.
export function stateIndexAt(events, cursor) {
  let s = 0;
  const end = Math.min(cursor, (events || []).length - 1);
  for (let i = 0; i <= end; i++) s = Math.max(s, stateForEvent(events[i]?.event_type));
  return s;
}

// Mirrors RUN_STATES / TERMINAL in backend services/canonical_work.py.
export const NON_TERMINAL_RUN_STATES = ["created", "authorized", "running", "cancellation_requested"];
export const TERMINAL_RUN_STATES = ["completed", "failed", "cancelled"];
export function isLiveEligible(status) {
  return NON_TERMINAL_RUN_STATES.includes(status);
}

const num = (v) => (typeof v === "number" && isFinite(v) ? v : undefined);

function metaValue(events, eventType, key) {
  const ev = (events || []).find((e) => e.event_type === eventType);
  return ev?.metadata?.[key];
}

// Build a generic, module-neutral decision graph from the REAL events + summary.
// Node counts come from event metadata (agent_count / candidate_count); nothing
// is invented. Agents are aggregated into clusters at scale; candidate nodes are
// the FEASIBLE treatments only (infeasible ones are excluded from the graph but
// still listed in SolverComparison), and the selected classical treatment is
// tagged verifiedClassical so it can paint white.
export function buildGraphModel({ module_id, summary, events, workUnits }) {
  const agentCount = num(metaValue(events, "mas.agents_mobilized", "agent_count")) ?? num(summary?.agent_count)
    ?? (Array.isArray(workUnits) ? workUnits.length : 0);
  const treatments = summary?.treatments || [];
  const feasible = feasibleCandidateTreatments(treatments);
  // Candidate count is backed, in order, by: feasible treatments on the summary →
  // the candidates.compared event → tool.started Work Units (each solver run yields
  // a candidate) → summary.candidate_count. With no backing record there are NO
  // candidate nodes — a candidate is never invented.
  const toolUnits = new Set((events || [])
    .filter((e) => /^(work\.scheduled|work\.started|tool\.started)$/.test(e.event_type) && e.work_unit_id)
    .map((e) => e.work_unit_id));
  const candCount = num(metaValue(events, "candidates.compared", "candidate_count"))
    ?? (toolUnits.size || undefined) ?? num(summary?.candidate_count) ?? 0;
  const candModels = feasible.length
    ? feasible.map((t, i) => ({ id: `cand-${i}`, label: t.name, verifiedClassical: !!(t.selected && t.engine_class === "classical") }))
    : Array.from({ length: candCount },
        (_, i) => ({ id: `cand-${i}`, label: `Candidate ${i + 1}`, verifiedClassical: false }));

  const clusters = aggregateAgents(agentCount);
  const nodes = [{ id: "source", type: "source", label: (module_id || "incident").toUpperCase() }];
  clusters.forEach((c) => nodes.push({ id: c.id, type: "agent", label: c.count > 1 ? `${c.count} agents` : "Agent", count: c.count }));
  candModels.forEach((c) => nodes.push({ id: c.id, type: "candidate", label: c.label, verifiedClassical: c.verifiedClassical }));
  nodes.push({ id: "decision", type: "decision", label: "Accepted decision" });

  const links = [];
  clusters.forEach((c) => links.push({ source: "source", target: c.id, kind: "mobilize" }));
  candModels.forEach((cand, i) => {
    const cl = clusters[i % Math.max(1, clusters.length)];
    if (cl) links.push({ source: cl.id, target: cand.id, kind: "propose" });
    // The verified-classical candidate's route to the decision is the SOLVER route
    // (eligible for cyan); every candidate also has a decision-comparison route.
    links.push({ source: cand.id, target: "decision", kind: "compare", solverRoute: cand.verifiedClassical });
  });

  const idIndex = new Map(nodes.map((n, i) => [n.id, i]));
  const simNodes = nodes.map((n) => ({ ...n }));
  const simLinks = links.map((l) => ({ source: idIndex.get(l.source), target: idIndex.get(l.target) }));
  const sim = forceSimulation(simNodes, 3)
    .force("charge", forceManyBody().strength(-42))
    .force("link", forceLink(simLinks).distance(2.4).strength(0.7))
    .force("center", forceCenter(0, 0, 0))
    .force("collide", forceCollide(0.9))
    .stop();
  for (let i = 0; i < 220; i++) sim.tick();

  const positioned = simNodes.map((n) => ({
    id: n.id, type: n.type, label: n.label,
    verifiedClassical: !!n.verifiedClassical, count: n.count || 1,
    pos: [n.x || 0, n.y || 0, n.z || 0],
  }));
  // Recenter on the CENTROID and normalize the spread so the whole graph frames
  // cleanly in the viewport regardless of node count.
  const cx = positioned.reduce((s, n) => s + n.pos[0], 0) / positioned.length;
  const cy = positioned.reduce((s, n) => s + n.pos[1], 0) / positioned.length;
  const cz = positioned.reduce((s, n) => s + n.pos[2], 0) / positioned.length;
  positioned.forEach((n) => { n.pos = [n.pos[0] - cx, n.pos[1] - cy, n.pos[2] - cz]; });
  const maxR = Math.max(0.001, ...positioned.map((n) => Math.hypot(n.pos[0], n.pos[1], n.pos[2])));
  const scale = 3.6 / maxR;
  positioned.forEach((n) => { n.pos = [n.pos[0] * scale, n.pos[1] * scale, n.pos[2] * scale]; });
  const posById = new Map(positioned.map((n) => [n.id, n.pos]));
  const laidLinks = links.map((l) => ({ ...l, a: posById.get(l.source), b: posById.get(l.target) }));

  return { nodes: positioned, links: laidLinks, agentCount, candidateCount: candModels.length };
}

// Structural signature of the graph: the layout is re-solved ONLY when a real
// record changes the node set (never on every incoming event), so live frames
// interpolate rather than teleport.
export function graphSignature({ module_id, summary, events, workUnits }) {
  const units = new Set((events || [])
    .filter((e) => /^(work\.scheduled|work\.started|tool\.started)$/.test(e.event_type) && e.work_unit_id)
    .map((e) => e.work_unit_id));
  return JSON.stringify([
    module_id || null,
    metaValue(events, "mas.agents_mobilized", "agent_count") ?? summary?.agent_count ?? (workUnits || []).length,
    metaValue(events, "candidates.compared", "candidate_count") ?? units.size,
    summary?.candidate_count ?? null,
    feasibleCandidateTreatments(summary?.treatments || []).map((t) => [t.name, !!t.selected, t.engine_class]),
  ]);
}

// Honesty: a Quantum badge appears ONLY when a verified D-Wave lift exists on the
// Evidence record. Dry-run demo runs (classical) therefore never show it.
export function quantumVerified(summary, evidence) {
  if (summary?.quantum_lift_verified === true) {
    return (evidence || []).some(
      (e) => e.status === "verified" && /dwave|d-wave|quantum/i.test(JSON.stringify(e)));
  }
  return false;
}

const UNAVAILABLE = "Unavailable";
export function outcomeMetrics(summary) {
  const s = summary || {};
  return [
    { label: "Engine class", value: s.engine_class ?? UNAVAILABLE },
    { label: "Selected engine", value: s.selected_engine ?? UNAVAILABLE },
    { label: "Candidates compared", value: s.candidate_count ?? UNAVAILABLE },
    { label: "Agents mobilized", value: s.agent_count ?? UNAVAILABLE },
  ];
}
